import React, { useContext, useCallback, useEffect } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'

// Context
import {
  AppContext,
  OverlayContext,
  RouterContext,
  ModalContext,
} from '../../../contexts'

// Hooks
import { useDelay } from '../../../hooks/useDelay'
import { useLoading } from '../../../hooks/loading'
import { useErrorPopup } from '../../../hooks/popup'

// Components
import {
  If,
  Unless,
  Show,
  LineLoader,
  Iframe,
  ErrorBoundary,
} from '../../../components'

import CatchAllError from './CatchAllError'
import Header from './Header'
import Footer from './Footer'
import Body from './Body'

// styles
import styles from './AnalyzeModal.module.scss'

// TODO: encapsulate more of this iframe logic in Iframe component
const AnalyzeModal = ({ onClose, withOverlay = false }) => {
  const { activeTab, setActiveTab } = useContext(AppContext)
  const { showPopup, popup: Popup } = useErrorPopup()

  const { showIframe, setShowIframe, iframeReady, setIframeReady, iframeSrc } =
    useContext(RouterContext)
  const { setShowInfoModal } = useContext(ModalContext)
  const { setOverlayIdHistory } = useContext(OverlayContext)

  const { isGlobalLoading } = useLoading()

  const isPastLoadingDelayOffset = useDelay(175)

  const handleTabClick = useCallback(
    activeTabValue => {
      setOverlayIdHistory([])
      setActiveTab(activeTabValue)
    },
    [setActiveTab, setOverlayIdHistory]
  )

  const handleIframeLoad = useCallback(() => {
    setIframeReady(true)
  }, [setIframeReady])

  useEffect(() => {
    setShowInfoModal(false)
    const listenForMessageEvents = event => {
      if (event.data.actionType === 'close') {
        setShowIframe(false)
      }
      if (event.data.actionType === 'openExternalLink') {
        window.open(event.data.link, '_blank')
      }
      if (event.data.actionType === 'sendEmailRequest') {
        window.open(`mailto:${event.data.link}`, '_self')
      }
    }

    window.addEventListener('message', listenForMessageEvents)

    return () => window.removeEventListener('message', listenForMessageEvents)
  }, [])

  return (
    <>
      <div
        className={classnames({ [styles.overlay]: withOverlay })}
        onClick={onClose}
      />
      <div
        id='analyzeModalRoot'
        className={classnames(styles.analyzeModalRoot, {
          [styles.analyzeModalRootFullHeight]: !withOverlay,
        })}
        onClick={e => e.stopPropagation()}
      >
        <ErrorBoundary fallbackComponent={CatchAllError} onClose={onClose}>
          <If condition={isGlobalLoading && isPastLoadingDelayOffset}>
            <LineLoader className={styles.centerLoading} />
          </If>

          <If condition={!isGlobalLoading && isPastLoadingDelayOffset}>
            {showPopup && <Popup onClose={onClose} />}

            <Unless condition={showPopup}>
              <Header
                activeTab={activeTab}
                onTabClick={e => handleTabClick(e.target.value)}
                onClose={onClose}
              />
              <Body />
              <Footer />
            </Unless>
          </If>

          <Show className={styles.iframeWrap} condition={showIframe}>
            {!iframeReady && <LineLoader className={styles.centerLoading} />}
            <Show className={styles.iframeWrap} condition={iframeReady}>
              <Iframe
                className={styles.iframe}
                title={'iframe'}
                src={iframeSrc}
                onLoad={handleIframeLoad}
              />
            </Show>
          </Show>
        </ErrorBoundary>
      </div>
    </>
  )
}

AnalyzeModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  withOverlay: PropTypes.bool,
  modalStyles: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
  }),
}

export default AnalyzeModal
